import { Button, CloseButton, SegmentedToggle } from '@wix/design-system';
import { ExternalLinkSmall, LayoutTwoColumnsSmall } from '@wix/wix-ui-icons-common';
import { debounce, noop } from 'lodash';
import React from 'react';

import { DocumentCard } from './document-card';
import { EditorCard } from './editor-card';
import { getLinkToPlayground } from './get-link-to-playground';
import { ResizableContainer } from './resizable-container';
import { parseWithFallback } from './safe-parse';
import { unreachable } from './unreachable';
import type { PluginSettings } from './use-parsed-plugins';
import { useResponsive } from './use-responsive';
import { ViewerCard } from './viewer-card';

type Layout = SplitLayoutState | SingleLayoutState;

type SplitLayoutState = {
  type: 'split';
  right: 'viewer' | 'editor';
};

type SingleLayoutState = {
  type: 'single';
  active: 'document' | 'viewer' | 'editor';
};

type ContentState = {
  remountKey: number;
  content: object;
  stringContent: string;
};

export const PlaygroundComponent = (props: {
  plugins: PluginSettings;
  initialContent: object;
  style?: React.CSSProperties;
  containerRef: HTMLDivElement;
  variant: 'api' | 'docs' | 'intro';
}) => {
  const [contentState, setContentState] = React.useState<ContentState>({
    remountKey: 1,
    content: props.initialContent,
    stringContent: JSON.stringify(props.initialContent, null, 2),
  });
  const isWide = useResponsive(600, props.containerRef);
  const onChange = ({
    content,
    stringContent,
    remountElements,
  }: {
    content: object;
    stringContent?: string;
    remountElements?: boolean;
  }) => {
    setContentState(prevState => ({
      remountKey: prevState.remountKey + (remountElements ? 1 : 0),
      content,
      stringContent: stringContent ?? JSON.stringify(content, null, 2),
    }));
  };

  const [layoutState, setLayout] = React.useState<Layout>({
    type: 'split',
    right: 'viewer',
  });

  // We have constrains on what layout can be per width and variant
  const layout: Layout = (() => {
    if (layoutState.type === 'single') {
      return layoutState;
    }
    if (props.variant === 'intro') {
      return { type: 'single', active: 'editor' };
    }
    if (props.variant === 'api' || !isWide) {
      return { type: 'single', active: 'document' };
    }
    return layoutState;
  })();

  return (
    <ResizableContainer minHeight={300} style={{ padding: '18px 0 32px', ...props.style }}>
      {layout.type === 'split' ? (
        <SplitLayout
          contentState={contentState}
          layout={layout}
          onChange={onChange}
          setLayout={setLayout}
          plugins={props.plugins}
        />
      ) : (
        <SingleLayout
          plugins={props.plugins}
          contentState={contentState}
          layout={layout}
          onChange={onChange}
          setLayout={setLayout}
          isWide={isWide}
          variant={props.variant}
        />
      )}
    </ResizableContainer>
  );
};

const SingleLayout: React.FC<{
  plugins: PluginSettings;
  contentState: ContentState;
  layout: SingleLayoutState;
  onChange: ({
    content,
    stringContent,
    remountElements,
  }: {
    content: object;
    stringContent?: string | undefined;
    remountElements?: boolean | undefined;
  }) => void;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  isWide: boolean;
  variant: 'api' | 'docs' | 'intro';
}> = ({ plugins, layout, onChange, setLayout, isWide, contentState, variant }) => {
  const footer = (
    <div
      className="w-full"
      style={{
        '--wds-color-border-standard-secondary': '#D6E6FE',
      }}
    >
      <SegmentedToggle
        selected={layout.active}
        onClick={(_event, value) =>
          setLayout({
            type: 'single',
            active: value as 'document' | 'viewer' | 'editor',
          })
        }
        size="small"
      >
        <SegmentedToggle.Button value="document">Document</SegmentedToggle.Button>
        <SegmentedToggle.Button value="viewer">Viewer</SegmentedToggle.Button>
        <SegmentedToggle.Button value="editor">Editor</SegmentedToggle.Button>
      </SegmentedToggle>
    </div>
  );

  const headerButtons = (
    <>
      {isWide && variant === 'docs' ? (
        <Button
          onClick={() =>
            setLayout({
              type: 'split',
              right: layout.active === 'editor' ? 'editor' : 'viewer',
            })
          }
          priority="secondary"
          size="small"
          prefixIcon={<LayoutTwoColumnsSmall />}
          skin="dark"
        >
          Side By Side
        </Button>
      ) : undefined}
      {variant === 'api' || variant === 'intro' ? (
        <Button
          as="a"
          prefixIcon={<ExternalLinkSmall />}
          priority="secondary"
          size="small"
          skin="dark"
          onClick={() => {
            window.open(getLinkToPlayground(contentState.stringContent, plugins), '_blank');
          }}
        >
          Full Playground
        </Button>
      ) : undefined}
    </>
  );

  switch (layout.active) {
    case 'document':
      return (
        <DocumentCard
          isAiLabsEnabled={false}
          editorRef={null}
          stringContent={contentState.stringContent}
          onChange={({ content: value }) => {
            const content = JSON.parse(value);
            onChange({ content, remountElements: true, stringContent: value });
          }}
          footer={footer}
          headerRightButtons={headerButtons}
          editorPlugins={plugins.editor}
        />
      );
    case 'viewer':
      return (
        <ViewerCard
          content={contentState.content}
          footer={footer}
          headerRightButtons={headerButtons}
          plugins={plugins.viewer}
        />
      );

    case 'editor':
      return (
        <EditorCard
          editorRef={noop}
          content={contentState.content}
          plugins={plugins.editor}
          footer={footer}
          editorRemountKey={contentState.remountKey}
          onChange={content => onChange({ content })}
          headerRightButtons={headerButtons}
        />
      );
    default:
      throw unreachable(layout.active);
  }
};

const SplitLayout: React.FC<{
  plugins: PluginSettings;
  contentState: ContentState;
  layout: SplitLayoutState;
  onChange: ({
    content,
    stringContent,
    remountElements,
  }: {
    content: object;
    stringContent?: string | undefined;
    remountElements?: boolean | undefined;
  }) => void;

  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
}> = ({ contentState: state, layout, onChange, setLayout, plugins }) => {
  // when codemirror and editor is in the page we have performance issues cause of often state sync
  const onChangeDebounced = React.useCallback(debounce(onChange, 1000), [onChange]);
  return (
    <div style={{ display: 'flex', gap: '8px', width: '100%', height: '100%' }}>
      <div style={{ height: '100%', width: 'calc(50% - 4px)' }}>
        <DocumentCard
          isAiLabsEnabled={false}
          editorRef={null}
          editorPlugins={plugins.editor}
          stringContent={state.stringContent}
          onChange={({ content: value, immediate }) => {
            const content = parseWithFallback(value, state.content);
            if (layout.right === 'editor' && !immediate) {
              onChangeDebounced({ content, remountElements: true, stringContent: value });
            } else {
              onChange({
                content,
                remountElements: immediate && layout.right === 'editor',
                stringContent: value,
              });
            }
          }}
        />
      </div>
      <div style={{ height: '100%', width: 'calc(50% - 4px)' }}>
        {(() => {
          const footer = (
            <div style={{ width: '100%' }}>
              <SegmentedToggle
                selected={layout.right}
                onClick={(_event, value) =>
                  setLayout({ type: 'split', right: value as 'viewer' | 'editor' })
                }
                size="small"
              >
                <SegmentedToggle.Button value="editor">Editor</SegmentedToggle.Button>
                <SegmentedToggle.Button value="viewer">Viewer</SegmentedToggle.Button>
              </SegmentedToggle>
            </div>
          );
          return layout.right === 'editor' ? (
            <EditorCard
              editorRef={noop}
              headerRightButtons={
                <CloseButton
                  size="large"
                  skin="dark"
                  onClick={() => {
                    setLayout({ type: 'single', active: 'document' });
                  }}
                />
              }
              footer={footer}
              content={state.content}
              editorRemountKey={state.remountKey}
              onChange={content => onChange({ content })}
              plugins={plugins.editor}
            />
          ) : (
            <ViewerCard
              plugins={plugins.viewer}
              headerRightButtons={
                <CloseButton
                  size="large"
                  skin="dark"
                  onClick={() => {
                    setLayout({ type: 'single', active: 'document' });
                  }}
                />
              }
              footer={footer}
              content={state.content}
            />
          );
        })()}
      </div>
    </div>
  );
};
